import { API } from "@/config/apiBase";
import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, BarChart3, RefreshCw, Search, Filter, 
  ChevronDown, ChevronUp, User, Calendar, Tag, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";

// Event name display mapping
const EVENT_LABELS = {
  upgrade_clicked: "Upgrade Clicked",
  cancel_clicked: "Cancel Clicked",
  stripe_checkout_started: "Checkout Started",
  ai_used: "AI Used",
  call_log_saved: "Call Logged",
  contact_created: "Contact Created",
  invite_accepted: "Invite Accepted",
  plan_changed: "Plan Changed",
};

// Event color mapping
const EVENT_COLORS = {
  upgrade_clicked: "bg-green-500/20 text-green-400",
  cancel_clicked: "bg-red-500/20 text-red-400",
  stripe_checkout_started: "bg-amber-500/20 text-amber-400",
  ai_used: "bg-purple-500/20 text-purple-400",
  call_log_saved: "bg-blue-500/20 text-blue-400",
  contact_created: "bg-cyan-500/20 text-cyan-400",
  invite_accepted: "bg-pink-500/20 text-pink-400",
  plan_changed: "bg-orange-500/20 text-orange-400",
};

const AnalyticsEvents = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  
  // Filters
  const [eventNameFilter, setEventNameFilter] = useState("all");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  
  // Metadata modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      if (response.data.is_admin) {
        setIsAdmin(true);
        fetchEvents();
      } else {
        toast.error("Access denied. Admin only.");
        navigate("/dashboard");
      }
    } catch (error) {
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEvents = async (filters = {}) => {
    setIsFiltering(true);
    try {
      const params = new URLSearchParams();
      const eventFilter = filters.event_name !== undefined ? filters.event_name : eventNameFilter;
      const userFilter = filters.user_id !== undefined ? filters.user_id : userIdFilter;
      
      if (eventFilter && eventFilter !== "all") {
        params.append("event_name", eventFilter);
      }
      if (userFilter) {
        params.append("user_id", userFilter);
      }
      
      const url = `${API}/admin/analytics/events${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await axios.get(url);
      
      setEvents(response.data.events || []);
      setSummary(response.data.summary || {});
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load events");
      console.error("Failed to fetch events:", error);
    } finally {
      setIsFiltering(false);
    }
  };

  const handleApplyFilters = () => {
    fetchEvents({ event_name: eventNameFilter, user_id: userIdFilter });
  };

  const handleClearFilters = () => {
    setEventNameFilter("all");
    setUserIdFilter("");
    fetchEvents({ event_name: "all", user_id: "" });
  };

  const handleViewMetadata = (event) => {
    setSelectedEvent(event);
    setShowMetadataModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEventLabel = (eventName) => {
    return EVENT_LABELS[eventName] || eventName;
  };

  const getEventColor = (eventName) => {
    return EVENT_COLORS[eventName] || "bg-gray-500/20 text-gray-400";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E07A5F] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#1a1a2e] text-white">
      {/* Header */}
      <header className="bg-[#16162a] border-b border-[#2a2a4a] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-[#E07A5F]" />
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
                  Events Stream
                </h1>
              </div>
            </div>
            <Button 
              onClick={() => fetchEvents()} 
              variant="ghost" 
              className="text-white hover:bg-white/10"
              disabled={isFiltering}
              data-testid="refresh-events-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isFiltering ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Summary Cards */}
        {Object.keys(summary).length > 0 && (
          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {Object.entries(summary).map(([eventName, count]) => (
              <div 
                key={eventName}
                className="bg-[#16162a] rounded-xl p-4 border border-[#2a2a4a] cursor-pointer hover:border-[#3a3a5a] transition-colors"
                onClick={() => {
                  setEventNameFilter(eventName);
                  fetchEvents({ event_name: eventName, user_id: userIdFilter });
                }}
                data-testid={`summary-card-${eventName}`}
              >
                <Badge className={`${getEventColor(eventName)} text-xs mb-2`}>
                  {getEventLabel(eventName)}
                </Badge>
                <p className="text-2xl font-bold">{count}</p>
              </div>
            ))}
          </motion.div>
        )}

        {/* Filters */}
        <motion.div 
          className="bg-[#16162a] rounded-2xl border border-[#2a2a4a] p-4 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="text-[#8a8aa3] text-xs mb-1 block">Event Name</label>
              <Select value={eventNameFilter} onValueChange={setEventNameFilter}>
                <SelectTrigger 
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white"
                  data-testid="event-name-filter"
                >
                  <SelectValue placeholder="All events" />
                </SelectTrigger>
                <SelectContent className="bg-[#16162a] border-[#2a2a4a]">
                  <SelectItem value="all" className="text-white">All events</SelectItem>
                  {Object.keys(EVENT_LABELS).map((key) => (
                    <SelectItem key={key} value={key} className="text-white">
                      {EVENT_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1">
              <label className="text-[#8a8aa3] text-xs mb-1 block">User ID</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8aa3]" />
                <Input
                  value={userIdFilter}
                  onChange={(e) => setUserIdFilter(e.target.value)}
                  placeholder="Filter by user ID..."
                  className="bg-[#0f0f1a] border-[#2a2a4a] text-white pl-10"
                  data-testid="user-id-filter"
                />
              </div>
            </div>
            
            <div className="flex items-end gap-2">
              <Button 
                onClick={handleApplyFilters}
                className="bg-[#E07A5F] hover:bg-[#D0694E] rounded-full"
                disabled={isFiltering}
                data-testid="apply-filters-btn"
              >
                <Filter className="w-4 h-4 mr-2" /> Apply
              </Button>
              <Button 
                onClick={handleClearFilters}
                variant="outline"
                className="border-[#2a2a4a] text-white hover:bg-white/10 rounded-full"
                disabled={isFiltering}
                data-testid="clear-filters-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Events Table */}
        <motion.div 
          className="bg-[#16162a] rounded-2xl border border-[#2a2a4a] overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="px-4 py-3 border-b border-[#2a2a4a] flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent Events</h2>
            <span className="text-sm text-[#8a8aa3]">{total} events</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="events-table">
              <thead className="bg-[#0f0f1a]">
                <tr>
                  <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Timestamp
                    </div>
                  </th>
                  <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">
                    <div className="flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Event
                    </div>
                  </th>
                  <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" /> User ID
                    </div>
                  </th>
                  <th className="text-left p-4 text-[#8a8aa3] text-sm font-medium">Plan</th>
                  <th className="text-right p-4 text-[#8a8aa3] text-sm font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, i) => (
                  <tr 
                    key={`${event.created_at}-${i}`} 
                    className="border-t border-[#2a2a4a] hover:bg-white/5"
                    data-testid={`event-row-${i}`}
                  >
                    <td className="p-4 text-[#8a8aa3] text-sm whitespace-nowrap">
                      {formatDate(event.created_at)}
                    </td>
                    <td className="p-4">
                      <Badge className={getEventColor(event.event_name)}>
                        {getEventLabel(event.event_name)}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <code 
                        className="bg-[#0f0f1a] px-2 py-1 rounded text-xs text-[#8a8aa3] cursor-pointer hover:text-white"
                        onClick={() => {
                          setUserIdFilter(event.user_id);
                          fetchEvents({ event_name: eventNameFilter, user_id: event.user_id });
                        }}
                        title="Click to filter by this user"
                      >
                        {event.user_id ? event.user_id.substring(0, 12) + "..." : "N/A"}
                      </code>
                    </td>
                    <td className="p-4">
                      <Badge className={
                        event.effective_plan === "family" ? "bg-purple-500/20 text-purple-400" :
                        event.effective_plan === "plus" ? "bg-[#E07A5F]/20 text-[#E07A5F]" :
                        "bg-gray-500/20 text-gray-400"
                      }>
                        {event.effective_plan || "free"}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      {event.metadata && Object.keys(event.metadata).length > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewMetadata(event)}
                          className="text-[#8a8aa3] hover:text-white hover:bg-white/10"
                          data-testid={`view-metadata-btn-${i}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      ) : (
                        <span className="text-[#8a8aa3] text-xs">No metadata</span>
                      )}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-[#8a8aa3]">
                      {isFiltering ? (
                        <div className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" /> Loading events...
                        </div>
                      ) : (
                        "No events found. Try adjusting filters or wait for user activity."
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>

      {/* Metadata Modal */}
      <Dialog open={showMetadataModal} onOpenChange={setShowMetadataModal}>
        <DialogContent className="bg-[#16162a] border-[#2a2a4a] text-white rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
              Event Details
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#8a8aa3] text-xs mb-1">Event</p>
                  <Badge className={getEventColor(selectedEvent.event_name)}>
                    {getEventLabel(selectedEvent.event_name)}
                  </Badge>
                </div>
                <div>
                  <p className="text-[#8a8aa3] text-xs mb-1">Timestamp</p>
                  <p className="text-sm">{formatDate(selectedEvent.created_at)}</p>
                </div>
                <div>
                  <p className="text-[#8a8aa3] text-xs mb-1">User ID</p>
                  <code className="bg-[#0f0f1a] px-2 py-1 rounded text-xs text-[#8a8aa3]">
                    {selectedEvent.user_id || "N/A"}
                  </code>
                </div>
                <div>
                  <p className="text-[#8a8aa3] text-xs mb-1">Plan</p>
                  <Badge className={
                    selectedEvent.effective_plan === "family" ? "bg-purple-500/20 text-purple-400" :
                    selectedEvent.effective_plan === "plus" ? "bg-[#E07A5F]/20 text-[#E07A5F]" :
                    "bg-gray-500/20 text-gray-400"
                  }>
                    {selectedEvent.effective_plan || "free"}
                  </Badge>
                </div>
              </div>
              
              <div>
                <p className="text-[#8a8aa3] text-xs mb-2">Metadata</p>
                <pre className="bg-[#0f0f1a] rounded-xl p-4 overflow-auto max-h-60 text-xs text-[#81B29A]">
                  {JSON.stringify(selectedEvent.metadata || {}, null, 2)}
                </pre>
              </div>
              
              <div className="flex justify-end pt-2">
                <Button 
                  onClick={() => setShowMetadataModal(false)}
                  className="bg-[#E07A5F] hover:bg-[#D0694E] rounded-full"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalyticsEvents;
